import {
    Box, Card, Link as MuiLink,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
    Checkbox,
    FormControlLabel,
    Select, 
    MenuItem,
    SelectChangeEvent,
    TextField
} from "@mui/material";
import React, {useEffect, useState} from "react";
import dateCardsService from "../../../services/dateCardsService";
import {IDateCardOrder} from "../../../types/DateCardOrders";
import { formatTimestamp } from "../../../utils/timestampFormatter";
import { DateCardOrderStatus } from "../../../constants/DateCardOrderStatus";

export const PendingDateCardList: React.FC<{
    pageSize?: 10 | 25 | 50 | 100;
}> = ({pageSize = 25}) => {
    const [page, setPage] = useState(0);
    const [dateCardOrders, setDateCardOrders] = useState<IDateCardOrder[]>([]);
    const [filteredOrders, setFilteredOrders] = useState<IDateCardOrder[]>([]);
    const [checkbox1Checked, setCheckboxValues] = useState<{ [key: string]: boolean }>({});
    const [dropdownValues, setDropdownValues] = useState<{ [key: string]: string }>({});
    const [searchTerm, setSearchTerm] = useState(""); // State for the search term

    const handleDeliveredClick = async (event: React.ChangeEvent<HTMLInputElement>, orderId: string, dateCardDocId: string) => {
        const checked = event.target.checked;
        setCheckboxValues(prevState => ({
            ...prevState,
            [orderId]: checked
        }));
        await dateCardsService.updateOrderStatus(orderId, dateCardDocId, checked ? DateCardOrderStatus.delivered : '');
        await getDateCardOrders();
    };

    const handlePendingRequestClick = async (event: SelectChangeEvent<string>, orderId: string, dateCardOrderId: string) => {
        const selectedValue = event.target.value;
        setDropdownValues(prevState => ({
            ...prevState,
            [orderId]: selectedValue
        }));
        await dateCardsService.updateOrderStatus(orderId, dateCardOrderId, selectedValue);
        await getDateCardOrders();
    };

    async function getDateCardOrders() {
        const dateCardOrders = await dateCardsService.getPendingDateCardOrders();
        const initialDropdownValues: { [key: string]: string } = {};
        const intitalCheckboxValues: { [key: string]: boolean } = {};
        dateCardOrders.forEach(order => {
            if (order.orderStatus === DateCardOrderStatus.delivered) {
                intitalCheckboxValues[order.orderID] = true;
                initialDropdownValues[order.orderID] = '';
            } else {
                intitalCheckboxValues[order.orderID] = false;
                initialDropdownValues[order.orderID] = order.orderStatus || '';
            }
        });
        setDropdownValues(initialDropdownValues);
        setCheckboxValues(intitalCheckboxValues);
        setDateCardOrders(dateCardOrders);
        setFilteredOrders(dateCardOrders); // Initialize filtered orders
    }

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const term = event.target.value.toLowerCase().trim();
        setSearchTerm(term);
        setFilteredOrders(dateCardOrders.filter(order =>
            order.profileUrl?.toLowerCase().includes(term)
        ));
    };

    useEffect(() => {
        void getDateCardOrders();
    }, []);

    return (
        <Box>
            <Typography variant={"h1"} sx={{ mb: 4 }}>
                Pending Date Card Orders
            </Typography>
            {/* Move Search Bar */}
            <Box sx={{ mb: 4 }}>
                <TextField
                    label="Search by Profile URL"
                    variant="outlined"
                    value={searchTerm}
                    onChange={handleSearch}
                    sx={{ width: '300px' }}
                />
            </Box>
    
            <Card sx={{ mb: 4 }}>
                <Box sx={{ width: '100%' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>First Name</TableCell>
                                    <TableCell>Last Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Location</TableCell>
                                    <TableCell>Design</TableCell>
                                    <TableCell>Card intro message</TableCell>
                                    <TableCell>Card name</TableCell>
                                    <TableCell>Order created</TableCell>
                                    <TableCell>Marked Pending</TableCell>
                                    <TableCell>Profile Url</TableCell>
                                    <TableCell>Order status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredOrders
                                    .sort((a, b) => {
                                        const dateA = a.timeStamp ? new Date(a.timeStamp).getTime() : new Date().getTime();
                                        const dateB = b.timeStamp ? new Date(b.timeStamp).getTime() : new Date().getTime();
                                        return dateA - dateB;
                                    })
                                    .slice(page * pageSize, page * pageSize + pageSize)
                                    .map((row) => (
                                        <TableRow
                                            key={row.orderID}
                                            sx={{ cursor: 'default', textDecoration: 'none' }}
                                        >
                                            <TableCell>{row.firstName}</TableCell>
                                            <TableCell>{row.lastName}</TableCell>
                                            <TableCell>{row.email}</TableCell>
                                            <TableCell>{row.address != null ? row.address : 'null'}</TableCell>
                                            <TableCell>
                                                {row.templateImageUrl ? (
                                                    <MuiLink href={row.templateImageUrl} target="_blank" onClick={(e) => e.stopPropagation()}>
                                                        {row.templateName ?? 'View design'}
                                                    </MuiLink>
                                                ) : row.cardColor}
                                            </TableCell>
                                            <TableCell>{row.cardMessage}</TableCell>
                                            <TableCell>{row.cardName}</TableCell>
                                            <TableCell>{formatTimestamp(row.timeStamp)}</TableCell>
                                            <TableCell>{formatTimestamp(row.markedPendingAt ?? '')}</TableCell>
                                            <TableCell>
                                                <MuiLink
                                                    href={row.profileUrl}
                                                    onClick={(e) => e.stopPropagation()}
                                                    target={row.profileUrl}
                                                >
                                                    {row.profileUrl}
                                                </MuiLink>
                                            </TableCell>
                                            <TableCell>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={checkbox1Checked[row.orderID] || false}
                                                            onChange={(e) =>
                                                                handleDeliveredClick(
                                                                    e,
                                                                    row.orderID,
                                                                    row.dateCardDocId
                                                                )
                                                            }
                                                            inputProps={{ 'aria-label': 'Delivered' }}
                                                        />
                                                    }
                                                    label="Accepted"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Select
                                                            value={dropdownValues[row.orderID]}
                                                            onChange={(e) =>
                                                                handlePendingRequestClick(
                                                                    e,
                                                                    row.orderID,
                                                                    row.dateCardDocId
                                                                )
                                                            }
                                                            displayEmpty
                                                            inputProps={{ 'aria-label': 'Pending Requests' }}
                                                        >
                                                            <MenuItem value={DateCardOrderStatus.default}>
                                                                Pending Request
                                                            </MenuItem>
                                                            <MenuItem value={DateCardOrderStatus.addressIssue}>
                                                                Address issue
                                                            </MenuItem>
                                                            <MenuItem value={DateCardOrderStatus.nickname}>
                                                                Nickname
                                                            </MenuItem>
                                                            <MenuItem value={DateCardOrderStatus.cardIntro}>
                                                                Card intro
                                                            </MenuItem>
                                                            <MenuItem value={DateCardOrderStatus.fullname}>
                                                                Full name
                                                            </MenuItem>
                                                            <MenuItem value={DateCardOrderStatus.other}>
                                                                Other
                                                            </MenuItem>
                                                        </Select>
                                                    }
                                                    label=""
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component={"div"}
                        count={filteredOrders.length}
                        page={page}
                        rowsPerPage={pageSize}
                        onPageChange={(event, newPage) => setPage(newPage)}
                    />
                </Box>
            </Card>
        </Box>
    );
};